import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import {
  ACCOUNT_REGIONS,
  getAccountInfoByDeviceId,
  getAccountInfoByEmail,
  getDeviceDataById,
  regionInputFor,
} from '../../api/accountInfo';
import { useAuth } from '../../auth/AuthContext';
import { VIEW_ROLES } from '../firmware/firmwareConstants';
import './accountSearch.css';

function formatDate(value) {
  if (!value) return 'Not available';
  const date = new Date(value);
  return Number.isNaN(date.getTime())
    ? value
    : new Intl.DateTimeFormat(undefined, { dateStyle: 'medium', timeStyle: 'short' }).format(date);
}

function readableRegion(region) {
  return String(region || 'Unknown region').replace(/_/g, ' ');
}

function valueOrUnavailable(value) {
  return value === null || value === undefined || value === '' ? 'Not available' : value;
}

function KeyValue({ label, value }) {
  return (
    <div className="account-key-value">
      <span>{label}</span>
      <strong>{valueOrUnavailable(value)}</strong>
    </div>
  );
}

function DeviceDetail({ device, onClose }) {
  if (!device) return null;
  const details = device.deviceData || {};
  const connection = details.connectionData || {};
  const manufacturer = details.manufacturer || {};
  const deviceInterface = details.interface || {};
  const location = details.location || {};

  return (
    <section className="device-detail" aria-live="polite">
      <div className="device-detail-header">
        <div>
          <span className="section-eyebrow">Selected device</span>
          <h3>{details.deviceName || details.deviceId || 'Device detail'}</h3>
          <p>{readableRegion(device.region)}</p>
        </div>
        <button className="icon-button" type="button" aria-label="Close device detail" title="Close device detail" onClick={onClose}>x</button>
      </div>

      <div className="device-detail-grid">
        <KeyValue label="Device ID" value={details.deviceId} />
        <KeyValue label="Child device ID" value={details.childDeviceId} />
        <KeyValue label="Connection" value={details.connectionStatus} />
        <KeyValue label="Device type" value={[details.deviceType, details.deviceSubType].filter(Boolean).join(' / ')} />
        <KeyValue label="Firmware" value={details.firmwareVersion} />
        <KeyValue label="Hardware" value={details.hardwareVersion} />
        <KeyValue label="Protocol" value={connection.protocol} />
        <KeyValue label="Connection interface" value={connection.interfaceType} />
        <KeyValue label="Manufacturer" value={manufacturer.name} />
        <KeyValue label="Model" value={manufacturer.model || deviceInterface.model} />
        <KeyValue label="Serial number" value={manufacturer.serialNumber || deviceInterface.serialNumber} />
        <KeyValue label="Location" value={[location.country, location.timezone?.id].filter(Boolean).join(' / ')} />
        <KeyValue label="Created" value={formatDate(details.createdAt)} />
        <KeyValue label="Last updated" value={formatDate(details.updatedAt)} />
      </div>
    </section>
  );
}

function DeviceRow({ device, region, onView, canViewFirmware }) {
  const status = String(device.deviceStatus || 'UNKNOWN').toLowerCase();
  const name = device.deviceName || 'Unnamed device';
  const firmwareSearch = new URLSearchParams({
    deviceId: device.deviceId || '',
    childDeviceId: device.childDeviceId || '',
    regionId: regionInputFor(region),
  });
  return (
    <tr>
      <td><strong>{name}</strong></td>
      <td><code>{device.deviceId || 'Not available'}</code></td>
      <td><code>{device.childDeviceId || 'Not available'}</code></td>
      <td><span className={`account-status status-${status}`}>{device.deviceStatus || 'Unknown'}</span></td>
      <td className="account-table-actions">
        <div className="account-device-actions">
          <button className="btn btn-link" type="button" onClick={() => onView(device, region)}>
            View details
          </button>
          {canViewFirmware && device.deviceId && device.childDeviceId ? (
            <Link className="btn btn-link" to={`/firmware?${firmwareSearch}`} title={`Open firmware update for ${name}`}>Firmware</Link>
          ) : null}
        </div>
      </td>
    </tr>
  );
}

function AccountResult({ result, onViewDevice, canViewFirmware }) {
  const user = result.userData || {};
  const sites = Array.isArray(result.siteData) ? result.siteData : [];

  return (
    <section className="account-result">
      <header className="account-result-header">
        <div>
          <span className="section-eyebrow">Account</span>
          <h3>{user.email || user.userId || 'Account record'}</h3>
        </div>
        <span className="region-pill">{readableRegion(result.region)}</span>
      </header>

      <div className="account-summary-grid">
        <KeyValue label="User ID" value={user.userId} />
        <KeyValue label="Email" value={user.email} />
        <KeyValue label="First name" value={user.firstName} />
        <KeyValue label="Last name" value={user.lastName} />
        <KeyValue label="Nickname" value={user.nickName} />
        <KeyValue label="Language" value={user.language} />
      </div>

      <div className="account-sites">
        <div className="account-subsection-header">
          <h4>Sites</h4>
          <span>{sites.length} {sites.length === 1 ? 'site' : 'sites'}</span>
        </div>
        {sites.length === 0 ? <p className="account-empty">No sites were returned for this account.</p> : sites.map((site) => {
          const sections = Array.isArray(site.sections) ? site.sections : [];
          return (
            <section className="account-site" key={site.siteId || site.siteName}>
              <div className="account-site-header">
                <div>
                  <h5>{site.siteName || 'Unnamed site'}</h5>
                  <p>{[site.siteAddress, site.city, site.state, site.country, site.zipCode].filter(Boolean).join(', ') || 'Address not available'}</p>
                </div>
                <span className="account-site-id">{site.siteId}</span>
              </div>
              {sections.map((section) => (
                <div className="account-section" key={section.sectionId || section.sectionName}>
                  <div className="account-section-header">
                    <h6>{section.sectionName || 'Unnamed section'}</h6>
                    <span>{(section.areas || []).length} areas</span>
                  </div>
                  {(section.areas || []).map((area) => {
                    const devices = Array.isArray(area.devices) ? area.devices : [];
                    return (
                      <div className="account-area" key={area.areaId || area.areaName}>
                        <div className="account-area-header">
                          <strong>{area.areaName || 'Unnamed area'}</strong>
                          <span>{area.hiddenArea ? 'Hidden' : area.defaultArea ? 'Default area' : 'Area'}</span>
                        </div>
                        {devices.length === 0 ? <p className="account-empty">No devices in this area.</p> : (
                          <div className="account-table-wrap">
                            <table className="account-table">
                              <thead><tr><th>Name</th><th>Device ID</th><th>Child device</th><th>Status</th><th><span className="sr-only">Actions</span></th></tr></thead>
                              <tbody>{devices.map((device) => <DeviceRow key={`${device.deviceId}-${device.childDeviceId || ''}`} device={device} region={result.region} onView={onViewDevice} canViewFirmware={canViewFirmware} />)}</tbody>
                            </table>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              ))}
            </section>
          );
        })}
      </div>
    </section>
  );
}

export default function AccountSearchPanel() {
  const { tokens, user } = useAuth();
  const canViewFirmware = VIEW_ROLES.includes(user?.activeRole);
  const [query, setQuery] = useState('');
  const [regionId, setRegionId] = useState('ALL');
  const [results, setResults] = useState([]);
  const [searched, setSearched] = useState(false);
  const [searching, setSearching] = useState(false);
  const [error, setError] = useState('');
  const [selectedDevice, setSelectedDevice] = useState(null);
  const [deviceLoading, setDeviceLoading] = useState(false);
  const [deviceError, setDeviceError] = useState('');

  const submitSearch = async (event) => {
    event.preventDefault();
    const value = query.trim();
    if (!value) {
      setError('Enter an email address or device ID.');
      return;
    }

    try {
      setSearching(true);
      setSearched(true);
      setError('');
      setSelectedDevice(null);
      setDeviceError('');
      const response = value.includes('@')
        ? await getAccountInfoByEmail(tokens?.idToken, { email: value, regionId })
        : await getAccountInfoByDeviceId(tokens?.idToken, { deviceId: value, regionId });
      setResults(response);
    } catch (requestError) {
      setResults([]);
      setError(requestError instanceof Error ? requestError.message : String(requestError));
    } finally {
      setSearching(false);
    }
  };

  const viewDevice = async (device, region) => {
    try {
      setDeviceLoading(true);
      setDeviceError('');
      setSelectedDevice(null);
      const detail = await getDeviceDataById(tokens?.idToken, {
        deviceId: device.deviceId,
        childDeviceId: device.childDeviceId,
        regionId: region,
      });
      if (!detail) throw new Error('No technical device data was returned.');
      setSelectedDevice(detail);
    } catch (requestError) {
      setDeviceError(requestError instanceof Error ? requestError.message : String(requestError));
    } finally {
      setDeviceLoading(false);
    }
  };

  return (
    <section className="account-search">
      <form className="account-search-form" onSubmit={submitSearch}>
        <label htmlFor="account-search-query">Search</label>
        <div className="account-search-controls">
          <input id="account-search-query" value={query} onChange={(event) => setQuery(event.target.value)} placeholder="Email address or device ID" autoComplete="off" />
          <select aria-label="Search region" value={regionId} onChange={(event) => setRegionId(event.target.value)}>
            {ACCOUNT_REGIONS.map((region) => <option key={region.value} value={region.value}>{region.label}</option>)}
          </select>
          <button className="btn btn-primary" type="submit" disabled={searching}>{searching ? 'Searching...' : 'Search'}</button>
        </div>
      </form>

      {error ? <div className="alert alert-error" role="alert">{error}</div> : null}
      {deviceError ? <div className="alert alert-error" role="alert">{deviceError}</div> : null}
      {deviceLoading ? <div className="account-loading">Loading device details...</div> : null}
      <DeviceDetail device={selectedDevice} onClose={() => setSelectedDevice(null)} />

      {searched && !searching && !error && results.length === 0 ? (
        <div className="account-no-results">No account or device records were found for this search.</div>
      ) : null}
      <div className="account-results">
        {results.map((result, index) => <AccountResult key={`${result.region || 'region'}-${index}`} result={result} onViewDevice={viewDevice} canViewFirmware={canViewFirmware} />)}
      </div>
    </section>
  );
}
